import React from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Mail, MessageCircle } from 'lucide-react';
import { Link } from 'react-router-dom';

const Contact = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-green-50">
      {/* Header */}
      <motion.header
        className="bg-white/80 backdrop-blur-sm border-b border-gray-100 pt-20"
        initial={{ opacity: 0, y: -50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
      >
        <div className="w-full max-w-[85%] mx-auto px-4 md:px-6 py-4 md:py-6">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-3 group">
              <ArrowLeft className="w-5 h-5 text-primary group-hover:text-primary-dark transition-colors duration-300" />
              <span className="text-primary group-hover:text-primary-dark transition-colors duration-300">Back to Home</span>
            </Link>
          </div>
        </div>
      </motion.header>

      {/* Main Content */}
      <main className="w-full max-w-[85%] mx-auto px-4 md:px-6 py-12 md:py-16">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <motion.span
            className="inline-block px-4 py-2 bg-green-50 border border-green-200 rounded-full text-primary text-sm font-medium mb-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            Get In Touch
          </motion.span>
          <h1 className="text-4xl lg:text-6xl font-bold mb-6 text-gray-900">
            Contact <span className="text-gradient">Us</span>
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Have questions? We're here to help 24/7. Reach out to us through any of the channels below.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {/* Email Card */}
          <motion.a
            href="mailto:support@smronline.org"
            className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100 card-hover group text-center"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <div className="w-16 h-16 bg-gradient-primary rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg shadow-primary/20">
              <Mail className="w-8 h-8 text-white" />
            </div>
            <h3 className="text-xl font-bold mb-2 text-gray-900 group-hover:text-primary transition-colors">
              Email Support
            </h3>
            <p className="text-gray-600 mb-4">
              For all queries and support
            </p>
            <span className="text-primary font-semibold text-lg">
              support@smronline.org
            </span>
          </motion.a>

          {/* Response Time Card */}
          <motion.div
            className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100 card-hover group text-center"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            viewport={{ once: true }}
          >
            <div className="w-16 h-16 bg-gradient-primary rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg shadow-primary/20">
              <MessageCircle className="w-8 h-8 text-white" />
            </div>
            <h3 className="text-xl font-bold mb-2 text-gray-900 group-hover:text-primary transition-colors">
              Quick Response
            </h3>
            <p className="text-gray-600 mb-4">
              We typically respond within
            </p>
            <span className="text-primary font-semibold text-lg">
              24 Hours
            </span>
          </motion.div>
        </div>

        {/* Additional Info */}
        <motion.div
          className="mt-16 text-center"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-3xl p-8 md:p-12 border border-green-100 max-w-2xl mx-auto">
            <h3 className="text-2xl font-bold mb-4 text-gray-900">
              Need Help?
            </h3>
            <p className="text-gray-600 mb-6">
              Our dedicated support team is available around the clock to assist you with any queries or issues.
            </p>
            <a
              href="mailto:support@smronline.org"
              className="inline-flex items-center gap-2 bg-gradient-primary text-white px-8 py-4 rounded-full font-semibold hover:shadow-lg hover:shadow-primary/40 transition-all duration-300 transform hover:scale-105"
            >
              <Mail className="w-5 h-5" />
              <span>Email Us Now</span>
            </a>
          </div>
        </motion.div>
      </main>
    </div>
  );
};

export default Contact;
